import { useAtom } from 'jotai';
import { hoveredApartmentAreaAtom } from '../states/planner';
import { storageUrl } from '../firebase';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSite from '../hooks/useSite';
import { useApartments } from '../hooks/useApartments';
import { getAnchor } from './CustomShapeArea';
import { StatusBanner } from './StatusBanner';

export const AreaApartmentHover = () => {
  const [hoveredArea] = useAtom(hoveredApartmentAreaAtom);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [site] = useSite();
  const apartments = useApartments();

  const updateSize = useCallback(() => {
    if (tooltipRef.current?.parentElement) {
      const parent = tooltipRef.current.parentElement;
      const { offsetWidth, offsetHeight } = tooltipRef.current;

      setTooltipSize({
        width: (offsetWidth / parent.offsetWidth) * 100,
        height: (offsetHeight / parent.offsetHeight) * 100,
      });
    }
  }, []);

  useEffect(() => {
    if (!tooltipRef.current?.parentElement) return;
    const parent = tooltipRef.current.parentElement;

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(parent);

    return () => observer.disconnect();
  }, [hoveredArea, updateSize]);

  if (!hoveredArea) return null;
  const svgArea = hoveredArea;

  const apartment = apartments.find(a => a.apartmentid === svgArea.reference_id);
  if (!apartment) {
    return <></>;
  }

  const tooltipplace = svgArea.tooltipplace;
  const anchor = getAnchor(tooltipplace, svgArea, tooltipSize);

  return (
    <div
      ref={tooltipRef}
      className={`pointer-events-none absolute transform rounded-md bg-white pb-2 pr-2 pt-2 shadow-lg transition-all duration-300 ease-out ${
        hoveredArea ? 'visible scale-100 opacity-95' : 'invisible scale-75 opacity-0'
      }`}
      style={{
        top: `${anchor.y}%`,
        left: `${anchor.x}%`,
      }}
    >
      <div className="w-64 flex-col pb-2">
        <ul className="flex items-end">
          <li className="flex h-10 w-20 items-center">
            <StatusBanner status={apartment.status} className="w-fit pl-1 pr-5 text-xs" />
          </li>
          <li className="flex-grow"></li>
          <li>
            <div className="text-lg font-bold">
              {apartment.rok} rok, {apartment.size} m<sup>2</sup>
            </div>
            <div className="-mt-1 text-xs">Nr. {apartment.apartmentid}</div>
          </li>
        </ul>
        <div className="mt-4 align-top">
          <img src={storageUrl(apartment.floorplanpicture)} alt="floorplanpicture" onLoad={updateSize} />
        </div>
        <ul className="flex items-end pl-4">
          {site?.type == 'ownership' || site?.type == 'condominium' ? (
            <li className="">
              <ul>
                <li className="mt-2 text-left font-bold">Pris</li>
                <li className="text-xs">
                  {apartment.price > 0 ? apartment.price.toLocaleString('sv-SE') + ' kr' : '-'}
                </li>
              </ul>
            </li>
          ) : null}
          {site?.type == 'rental' ? (
            <li className="">
              <ul>
                <li className="mt-2 text-left font-bold">Hyra</li>
                <li className="text-xs">
                  {apartment.charge > 0 ? apartment.charge.toLocaleString('sv-SE') + ' kr/mån' : '-'}
                </li>
              </ul>
            </li>
          ) : null}
          <li className="flex-grow"></li>
          {site?.type == 'condominium' ? (
            <li className="">
              <ul>
                <li className="mt-2 text-left font-bold">Avgift</li>
                <li className="text-xs">
                  {apartment.charge > 0 ? apartment.charge.toLocaleString('sv-SE') + ' kr/mån' : '-'}
                </li>
              </ul>
            </li>
          ) : null}
          {site?.type == 'ownership' && apartment.lotsize ? (
            <li className="">
              <ul>
                <li className="mt-2 text-left font-bold">Tomtarea</li>
                <li className="text-xs">
                  {apartment.lotsize.toLocaleString('sv-SE')} m<sup>2</sup>
                </li>
              </ul>
            </li>
          ) : null}
          {site?.type == 'rental' && apartment.moveindate ? (
            <li className="">
              <ul>
                <li className="mt-2 text-left font-bold">Inflyttning</li>
                <li className="text-xs">
                  {apartment.moveindate.toDate().toLocaleString('sv-SE', { dateStyle: 'short' })}
                </li>
              </ul>
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
};
